import React, { useEffect, useMemo, useState } from 'react';
import { DeviceSoftwareInventoryReadDto, InventoryVersionGroupDto, Page, UserDto } from '@ampsec/platform-client';
import { Link } from 'react-router-dom';
import { getGridData } from 'Apis/library';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import { USERS_DASHBOARD_ROUTE } from 'Core-utils/routes';
import { MiniBar } from 'Components/atoms/MiniBar';
import SourcePill from 'Components/atoms/SourcePill/SourcePill';
import { sourceMeta } from './sourceMeta';

/**
 * What the user drilled into from the grouped grid (APD-1755):
 * - software: a title → its versions (newest-first) + the devices running it.
 * - asset: a device → the software installed on it.
 */
export type InventorySelection = { mode: 'software'; name: string } | { mode: 'asset'; sn: string; uid: string | null };

interface InventoryDetailPanelProps {
    selection: InventorySelection;
    onBack: () => void;
}

const CVE_BANNER = 'CVE & risk enrichment is planned (APD-1650) — no vulnerability data yet.';

const formatDate = (value: string | null | undefined): string => (value ? DateTimeFormatter(String(value)) : '—');

const ownerLabel = (user: UserDto | undefined, uid: string): string =>
    user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.emails?.[0] || uid : uid;

// Newest-first: compare numeric segments, then fall back to string order.
const compareVersionsDesc = (a: string | null | undefined, b: string | null | undefined): number => {
    const pa = (a ?? '')
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map(Number);
    const pb = (b ?? '')
        .split(/[^0-9]+/)
        .filter(Boolean)
        .map(Number);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i += 1) {
        const diff = (pb[i] ?? 0) - (pa[i] ?? 0);
        if (diff !== 0) {
            return diff;
        }
    }
    return (b ?? '').localeCompare(a ?? '');
};

const InventoryDetailPanel: React.FC<InventoryDetailPanelProps> = ({ selection, onBack }) => {
    const [versions, setVersions] = useState<InventoryVersionGroupDto[]>([]);
    const [devices, setDevices] = useState<DeviceSoftwareInventoryReadDto[]>([]);
    const [usersByUid, setUsersByUid] = useState<Record<string, UserDto>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        (async () => {
            try {
                if (selection.mode === 'software') {
                    const [versionPage, devicePage] = await Promise.all([
                        getGridData('deviceSoftwareInventory', { name: selection.name }, 'groupedVersions') as Promise<
                            Page<InventoryVersionGroupDto>
                        >,
                        getGridData('deviceSoftwareInventory', { name: selection.name, limit: 100 }, 'list') as Promise<
                            Page<DeviceSoftwareInventoryReadDto>
                        >,
                    ]);
                    if (active) {
                        setVersions(versionPage?.data ?? []);
                        setDevices(devicePage?.data ?? []);
                    }
                } else {
                    const devicePage = (await getGridData(
                        'deviceSoftwareInventory',
                        { sn: selection.sn, limit: 250 },
                        'list',
                    )) as Page<DeviceSoftwareInventoryReadDto>;
                    if (active) {
                        setVersions([]);
                        setDevices(devicePage?.data ?? []);
                    }
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [selection]);

    const deviceUids = useMemo(
        () => Array.from(new Set(devices.map((row) => row.uid).filter((uid): uid is string => Boolean(uid)))),
        [devices],
    );

    useEffect(() => {
        if (deviceUids.length === 0) {
            setUsersByUid({});
            return undefined;
        }
        let active = true;
        (async () => {
            const userPage = (await getGridData('users', { id: { $in: deviceUids } }, 'list')) as Page<UserDto>;
            if (active) {
                const byUid: Record<string, UserDto> = {};
                for (const user of userPage?.data ?? []) {
                    if (user.id) {
                        byUid[user.id] = user;
                    }
                }
                setUsersByUid(byUid);
            }
        })();
        return () => {
            active = false;
        };
    }, [deviceUids]);

    const sortedVersions = useMemo(() => [...versions].sort((a, b) => compareVersionsDesc(a.version, b.version)), [
        versions,
    ]);
    const maxVersionDevices = useMemo(
        () => sortedVersions.reduce((max, v) => (v.deviceCount > max ? v.deviceCount : max), 0),
        [sortedVersions],
    );

    const title = selection.mode === 'software' ? selection.name : selection.sn;
    const subtitle =
        selection.mode === 'software' ? (
            `${devices.length} installs · ${versions.length} version${versions.length === 1 ? '' : 's'}`
        ) : selection.uid ? (
            <>
                Owner{' '}
                <Link className="inventory-detail__owner-link" to={`${USERS_DASHBOARD_ROUTE}/${selection.uid}`}>
                    {ownerLabel(usersByUid[selection.uid], selection.uid)}
                </Link>
            </>
        ) : (
            'Unassigned device'
        );

    return (
        <div className="inventory-detail d-flex flex-column gap-3 h-100">
            <button
                type="button"
                className="inventory-detail__back btn btn-sm btn-link p-0 align-self-start"
                onClick={onBack}
            >
                ← Back to inventory
            </button>
            <div className="inventory-detail__header">
                <h3 className="inventory-detail__title">{title}</h3>
                <span className="inventory-detail__subtitle">{subtitle}</span>
            </div>

            <div className="inventory-detail__cve-banner">
                <span className="inventory-cve__dot" />
                {CVE_BANNER}
            </div>

            {loading ? (
                <span className="text-text-low-emphasis">Loading…</span>
            ) : (
                <div className="inventory-detail__columns">
                    {selection.mode === 'software' && (
                        <section className="inventory-detail__col">
                            <div className="inventory-detail__col-head">
                                <h4 className="inventory-detail__col-title">Versions</h4>
                                <span className="inventory-detail__col-count">{sortedVersions.length}</span>
                            </div>
                            <div className="inventory-detail__list">
                                {sortedVersions.length === 0 ? (
                                    <div className="inventory-detail__empty">No versions</div>
                                ) : (
                                    sortedVersions.map((version, index) => (
                                        <div
                                            className="inventory-detail__row"
                                            key={version.version ?? `unknown-${index}`}
                                        >
                                            <div className="inventory-detail__row-main">
                                                <span className="inventory-detail__row-title">
                                                    <span className="inventory-detail__version">
                                                        {version.version ?? '—'}
                                                    </span>
                                                    {index === 0 && version.version && (
                                                        <span className="inventory-detail__latest">Latest</span>
                                                    )}
                                                </span>
                                                <span className="inventory-detail__row-sub">
                                                    Last seen {formatDate(version.lastSeenAt)}
                                                </span>
                                            </div>
                                            <div className="inventory-detail__row-bar">
                                                <MiniBar value={version.deviceCount} max={maxVersionDevices} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    )}

                    <section className="inventory-detail__col">
                        <div className="inventory-detail__col-head">
                            <h4 className="inventory-detail__col-title">
                                {selection.mode === 'software' ? 'Devices' : 'Installed software'}
                            </h4>
                            <span className="inventory-detail__col-count">{devices.length}</span>
                        </div>
                        <div className="inventory-detail__list">
                            {devices.length === 0 ? (
                                <div className="inventory-detail__empty">Nothing found</div>
                            ) : (
                                devices.map((row) =>
                                    selection.mode === 'software' ? (
                                        <div className="inventory-detail__row" key={row.id}>
                                            <div className="inventory-detail__row-main">
                                                <span className="inventory-detail__row-title">{row.sn}</span>
                                                <span className="inventory-detail__row-sub">
                                                    {row.uid ? (
                                                        <Link
                                                            className="inventory-detail__owner-link"
                                                            to={`${USERS_DASHBOARD_ROUTE}/${row.uid}`}
                                                        >
                                                            {ownerLabel(usersByUid[row.uid], row.uid)}
                                                        </Link>
                                                    ) : (
                                                        'Unassigned'
                                                    )}
                                                    {' · v' + (row.version ?? '—')}
                                                </span>
                                            </div>
                                            <span className="inventory-detail__col-count">
                                                {formatDate(row.lastSeenAt)}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="inventory-detail__row" key={row.id}>
                                            <div className="inventory-detail__row-main">
                                                <span className="inventory-detail__row-title">{row.name}</span>
                                                <span className="inventory-detail__row-sub">
                                                    {'v' +
                                                        (row.version ?? '—') +
                                                        (row.publisher ? ' · ' + row.publisher : '')}
                                                </span>
                                            </div>
                                            {row.category && (
                                                <SourcePill
                                                    label={sourceMeta(row.category).label}
                                                    icon={sourceMeta(row.category).icon}
                                                    tone={sourceMeta(row.category).tone}
                                                    title={row.category}
                                                />
                                            )}
                                        </div>
                                    ),
                                )
                            )}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default InventoryDetailPanel;
